import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { USER_DETAIL } from "../../utils/constants";
import { ApiEndPoints } from "../../utils/endpoints";
import { httpPost } from "../../services/httpService";
import { Toast } from "primereact/toast";
import { REIToastContainer, REIAccountUpdateDisclaimer } from "../../components/ui";

interface PasswordRequirement {
  id: string;
  text: string;
  validator: (password: string, confirmPassword?: string) => boolean;
  valid: boolean;
}
const ChangePassword: React.FC = () => {
  const userDetails = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const toastRef = useRef<Toast>(null);
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
    {
      id: "match",
      text: "New Password and Confirm Password must be the same",
      validator: (pwd, confirmPwd) => pwd === confirmPwd && pwd !== "",
      valid: false,
    },
    {
      id: "length",
      text: "Must be at least 8 characters in length",
      validator: (pwd) => pwd.length >= 8,
      valid: false,
    },
    {
      id: "uppercase",
      text: "Minimum 1 upper case character (e.g. A)",
      validator: (pwd) => /[A-Z]/.test(pwd),
      valid: false,
    },
    {
      id: "lowercase",
      text: "Minimum 1 lower case character (e.g. a)",
      validator: (pwd) => /[a-z]/.test(pwd),
      valid: false,
    },
    {
      id: "numeric",
      text: "Minimum 1 numeric character (e.g. 1)",
      validator: (pwd) => /[0-9]/.test(pwd),
      valid: false,
    },
    {
      id: "special",
      text: "Minimum 1 special character (e.g. $)",
      validator: (pwd) => /[^A-Za-z0-9]/.test(pwd),
      valid: false,
    },
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    setPasswordRequirements((prevReqs) =>
      prevReqs.map((req) => ({
        ...req,
        valid: req.validator(form.newPassword, form.confirmPassword),
      }))
    );
  }, [form]);

  const handleReset = () => {
    setForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const allRequirementsMet = passwordRequirements.every((req) => req.valid);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (allRequirementsMet) {
      const postData = {
        EmailAddress: userDetails.EmailAddress,
        CurrentPassword: form.currentPassword,
        NewPassword: form.newPassword,
        ConfirmPassword: form.confirmPassword,
        btnUpdate: "Save",
        btnReset: "Reset",
      };

      await httpPost(ApiEndPoints.changeEmail + "/changepassword", postData).then((response) => {
        if (response.status == 200) {
          setForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          toastRef?.current?.show({
            severity: "success",
            summary: "Password change successfully.",
            detail: response?.error,
            life: 3000,
          });
          setLoading(false);
        } else {
          setForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          toastRef?.current?.show({
            severity: "error",
            summary: "Getting error while updating password.",
            detail: response?.error,
            life: 3000,
          });
          setLoading(false);
        }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <REIToastContainer toastReference={toastRef} />
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        <div style={{ flex: 1, marginRight: 32 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: 8 }}>Current Password</label>
            <InputText
              id="cpass-txt-current"
              data-testid="cpass-txt-current"
              type="password"
              name="currentPassword"
              value={form.currentPassword}
              onChange={handleChange}
              placeholder="Current Password"
              style={{ width: "100%" }}
              required
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: 8 }}>New Password</label>
            <InputText
              id="cpass-txt-new"
              data-testid="cpass-txt-new"
              type="password"
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              placeholder="New Password"
              style={{ width: "100%" }}
              required
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: "bold", display: "block", marginBottom: 8 }}>Confirm Password</label>
            <InputText
              id="cpass-txt-confirm"
              data-testid="cpass-txt-confirm"
              type="password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm Password"
              style={{ width: "100%" }}
              required
            />
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontWeight: "bold", display: "block", marginBottom: 8 }}>Password Requirements</label>
          <ul style={{ marginTop: 0, paddingLeft: 20 }}>
            {passwordRequirements.map((req) => (
              <li
                key={req.id}
                style={{
                  color: req.valid ? "green" : "inherit",
                  listStyleType: "none",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: 8 }}>{req.valid ? "✓" : "○"}</span>
                {req.text}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <REIAccountUpdateDisclaimer />
      <div style={{ textAlign: "left" }}>
        <Button
          id="cpass-btn-save"
          data-testid="cpass-btn-save"
          type="submit"
          label="Save"
          style={{
            marginRight: 16,
            opacity: allRequirementsMet ? 1 : 0.6,
            cursor: allRequirementsMet ? "pointer" : "not-allowed",
          }}
          disabled={!allRequirementsMet}
        />
        <Button id="cpass-btn-reset" data-testid="cpass-btn-reset" type="button" label="Reset" className="p-button-secondary" onClick={handleReset} />
      </div>
    </form>
  );
};

export default ChangePassword;
